// /school-chains — the school-group / trust marketing page (PGS-15). The Enterprise group
// capability shipped long ago (Phase-3 GRP: group console, cross-school roll-up, consolidated
// billing, governed transfers, branding inheritance) but no marketing surface mentioned it.
//
// EVERY claim here uses the approved wording of docs/marketing/CLAIMS_REGISTRY.md rows GRP1–GRP6
// (added 2026-08-18, before this page shipped). Do not extend the story without a registry row.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowRightLeft, Check, IndianRupee, LayoutDashboard, Network, Palette, ShieldCheck,
} from "lucide-react";
import { ContactSalesModal, MarketingTopBar, SiteFooter } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import { Stagger, StaggerItem } from "./landingparts/parts1";
import MarketingSeo from "../components/marketing/MarketingSeo";
import BrandWordmarkText from "../components/BrandWordmarkText";
import { Button } from "../components/ui/primitives";
import { init as initAnalytics, track } from "../lib/analytics";

// GRP2–GRP5 — the four shipped group capabilities, registry wording only.
const CAPABILITIES = [
  {
    id: "console",
    icon: LayoutDashboard,
    accent: "text-primary bg-primary/10",
    title: "One group console",
    body:
      "One console rolls up the group's member schools for authorised group administrators — "
      + "roster and cross-school view without signing into each school separately.",
    note: "Read and export scope for the group role; each school's day-to-day workflows stay with that school's own staff.",
  },
  {
    id: "billing",
    icon: IndianRupee,
    accent: "text-orange-dark bg-orange-soft",
    title: "Consolidated billing",
    body:
      "Generate one monthly group statement with per-school lines in exact paise and a CSV export "
      + "— the trust's finance office reconciles one document, not one per school.",
  },
  {
    id: "transfers",
    icon: ArrowRightLeft,
    accent: "text-teal-dark bg-teal/10",
    title: "Governed student transfers",
    body:
      "Move a student between the group's schools through a consent-gated request that an "
      + "authorised person approves; the student's identity record carries over.",
    note: "A transfer is never automatic — it is requested, reviewed and approved, with the source enrolment closed on approval.",
  },
  {
    id: "branding",
    icon: Palette,
    accent: "text-ai-ink bg-ai/10",
    title: "Group branding inheritance",
    body:
      "Set the trust's brand once; a member school inherits it unless it publishes its own — "
      + "logo, accent and identity resolve school → group → default.",
  },
];

export default function SchoolChainsPage() {
  const navigate = useNavigate();
  const [salesOpen, setSalesOpen] = useState(false);

  useEffect(() => {
    initAnalytics();
    window.scrollTo(0, 0);
  }, []);

  return (
    <MarketingMotion>
    <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page" data-testid="school-chains-page">
      <MarketingSeo
        title="School Chain & Group Management | Vidyapeeth360"
        description="Run a trust or school group on one platform — one console across your member schools, cross-school roll-ups, consolidated billing and approved transfers."
        path="/school-chains"
      />
      <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />
      <main id="main" className="flex-1">
        {/* Header */}
        <section className="mx-auto max-w-6xl px-5 pt-12 sm:pt-16 pb-10">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-ink-2">
              <Network className="w-4 h-4 text-primary" aria-hidden="true" /> School groups &amp; chains
            </p>
            <h1 className="mt-4 font-heading font-bold text-3xl sm:text-[40px] leading-[1.12] text-ink tracking-tight">
              Run every school in your trust on one governed platform.
            </h1>
            <p className="mt-4 text-ink-2 text-[16px] leading-relaxed max-w-2xl">
              Each school on <BrandWordmarkText /> keeps its own isolated workspace, staff and data.
              The group layer sits above them: a scoped console for the trust&rsquo;s
              administrators, one consolidated statement for its finance office, and governed
              movement of students between member schools.
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-2">
              {[
                "Per-school isolation stays intact",
                "Group scope limited to your own trust",
                "Enterprise-plan capability",
              ].map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-positive shrink-0" aria-hidden="true" /> {t}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => { track("demo_request", { placement: "school_chains_page" }); setSalesOpen(true); }} data-testid="school-chains-demo">
                Book a demo <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/pricing")} data-testid="school-chains-pricing">
                See what Enterprise includes
              </Button>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="border-y border-line bg-surface" aria-labelledby="school-chains-caps-h">
          <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
            <h2 id="school-chains-caps-h" className="font-heading font-bold text-2xl sm:text-[28px] text-ink tracking-tight max-w-2xl">
              What the group layer actually does.
            </h2>
            <Stagger className="mt-8 grid gap-5 md:grid-cols-2" stagger={0.05} amount={0.1} data-testid="school-chains-grid">
              {CAPABILITIES.map((item) => (
                <StaggerItem as="article" key={item.id} className="rounded-2xl border border-line bg-canvas p-6" data-testid={`school-chains-${item.id}`}>
                  <span className={`w-11 h-11 rounded-xl grid place-items-center ${item.accent}`}>
                    <item.icon className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading font-semibold text-ink text-[16px] leading-snug">{item.title}</h3>
                  <p className="mt-2 text-[14px] text-ink-2 leading-relaxed">{item.body}</p>
                  {item.note && <p className="mt-3 text-[13px] leading-relaxed text-ink-subtle">{item.note}</p>}
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* Boundary — GRP1/GRP6 */}
        <section className="mx-auto max-w-6xl px-5 py-12 sm:py-16" aria-labelledby="school-chains-boundary-h">
          <div className="max-w-3xl rounded-2xl border border-primary/20 bg-primary/[0.05] p-6 sm:p-7" data-testid="school-chains-boundary">
            <div className="flex items-start gap-3.5">
              <span className="mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0 text-primary bg-primary/10">
                <ShieldCheck className="w-5 h-5" aria-hidden="true" />
              </span>
              <h2 id="school-chains-boundary-h" className="font-heading font-semibold text-xl text-ink leading-snug">
                The boundary, in one sentence
              </h2>
            </div>
            <p className="mt-3 text-[15px] text-ink-2 leading-relaxed">
              A group administrator sees only their own group&rsquo;s member schools — never another
              group&rsquo;s — and each member school&rsquo;s data stays walled off in its own
              database-isolated workspace underneath.
            </p>
            <p className="mt-3 text-[13.5px] text-ink-subtle leading-relaxed">
              Group and chain capability is part of the Enterprise plan; the final scope for your
              trust is confirmed in the written proposal. The isolation story behind it is on the{" "}
              <Link to="/security" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">Security overview</Link>.
            </p>
          </div>
        </section>

        {/* Close */}
        <section className="mx-auto max-w-6xl px-5 pb-14 sm:pb-16">
          <div className="rounded-3xl bg-gradient-to-br from-[var(--vp-close-band-from)] via-[var(--vp-close-band-via)] to-[var(--vp-close-band-to)] p-8 sm:p-12 text-center relative overflow-hidden">
            <h2 className="font-heading font-bold text-[26px] sm:text-[32px] text-white tracking-tight max-w-2xl mx-auto leading-tight">
              One trust. Many schools. One accountable view.
            </h2>
            <p className="text-white/75 mt-3 max-w-xl mx-auto leading-relaxed">
              Bring your group&rsquo;s structure to a walkthrough — we&rsquo;ll map member schools,
              billing and transfer policy against the shipped group capability.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-lg" onClick={() => { track("demo_request", { placement: "school_chains_page_close" }); setSalesOpen(true); }}>
                Book a demo <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button size="lg" variant="ghost" className="text-white border border-white/40 hover:bg-white/10" onClick={() => navigate("/contact")}>
                Talk to sales
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} contextLabel="school groups, trusts and chains" placement="school_chains_page" />
    </div>
    </MarketingMotion>
  );
}
